import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { useParams } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { proxy, useSnapshot } from 'valtio';
import type { OrganizationDetail, TLoadedState } from '~/common/types';
import EmailLink from '~/components2/EmailLink';
import { GlobalFilter, Pagination, TableV2 } from '~/components2/Table';
import { useGetProject } from '~/hooks/useGetProject';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';
import { manholeColumns, manholeColumnVisibilityState, potholeColumns, potholeColumnVisibilityState } from './columns';
import { CivilGridMobileCode, filterGeoItemsByReportType, getIsError, getIsLoading, useListGeoItems } from './common';
import { ExportDropdown } from './ExportDropdown';
import { PotholeDialogHost, PotholeMapLayers } from './PotholeMapLayers';
import { usePotholeDeepLink } from './usePotholeDeepLink';

type TStore = { selectedRowIds: Set<string> };

const store = proxy<TStore>({
  selectedRowIds: new Set(),
});

export function MapChildren() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  // Load-gated getter: avoid treating a still-loading org (undefined mobileReportType)
  // as potholeLog and flashing empty markers for manhole orgs.
  const { currentOrganization: org, currentOrganizationId } = useSnapshot(rootStore);
  const { selectedRowIds } = useSnapshot(store);
  const project = useGetProject({ projectId }, { refetchOnWindowFocus: false });
  // The current org starts as the last one used and is switched to the project's only after the
  // project loads; until they agree, the report-type filter would hide this project's records.
  const orgReady = !!org && currentOrganizationId === project.data?.organization;
  const _geoItems = useListGeoItems({ projectId, enabled: orgReady });

  const geoItems = useMemo(
    () => (orgReady ? filterGeoItemsByReportType(_geoItems.data ?? [], org.mobileReportType) : []),
    [_geoItems.data, org, orgReady],
  );

  // isSuccess, not a non-empty list: a project with no records must still resolve the param,
  // and a failed load must not report the record missing.
  usePotholeDeepLink(projectId, geoItems, _geoItems.isSuccess && orgReady);

  if (!orgReady) return null;

  return (
    <>
      <PotholeMapLayers
        geoItems={geoItems}
        mobileReportType={org.mobileReportType}
        selectedRowIds={selectedRowIds}
        surface="tab"
      />
      <PotholeDialogHost geoItems={geoItems} mobileReportType={org.mobileReportType} surface="tab" />
    </>
  );
}

export function TabDetails() {
  const { currentOrganizationDetail } = useSnapshot(rootStore);
  const o = currentOrganizationDetail as TLoadedState & OrganizationDetail;
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { projectDetailsMap: map } = MapGL.useMap();

  const geoItems = useListGeoItems({ projectId });
  const project = useGetProject({ projectId }, { refetchOnWindowFocus: false });

  const columns = useMemo(() => {
    if (o.mobileReportType === 'manholeInspection') {
      return manholeColumns;
    } else {
      return potholeColumns;
    }
  }, [o.mobileReportType]);

  const columnVisibility = useMemo(() => {
    if (o.mobileReportType === 'manholeInspection') {
      return manholeColumnVisibilityState;
    } else {
      return potholeColumnVisibilityState;
    }
  }, [o.mobileReportType]);

  const data = useMemo(
    () => filterGeoItemsByReportType(geoItems.data ?? [], o.mobileReportType),
    [geoItems.data, o.mobileReportType],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId: (row) => row.id,
    state: { columnVisibility },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 100 },
    },
  });

  const rowSelection = table.getState().rowSelection;

  useEffect(() => {
    store.selectedRowIds = new Set(Object.keys(rowSelection));
  }, [rowSelection]);

  const isLoading = getIsLoading([geoItems, project]);
  const isError = getIsError([geoItems, project]);

  if (isLoading) return <SkeletonSpinnerCentered />;
  if (isError) {
    return (
      <div className="text-sm font-noto">
        An error occurred while retrieving your date. Please{' '}
        <EmailLink className="text-blue-50 underline hover:text-blue-60">contact support</EmailLink>.
      </div>
    );
  }

  if (!data.length) return <CivilGridMobileCode />;

  return (
    <div className="grid grid-cols-1 gap-2 overflow-auto">
      <div className="flex w-full justify-end">
        <ExportDropdown table={table} project={project.data!} />
      </div>

      <TableV2
        id="potholing-table"
        table={table}
        onCellClick={(cell) => {
          const id = cell.column.id;
          if (id !== 'select') {
            const item = cell.row.original;
            const [lng, lat] = item.geometry.coordinates;
            map?.flyTo({
              center: { lng, lat },
              duration: 1_000,
            });
          }
        }}
        topSection={
          <div className="flex items-end justify-between pb-2">
            <Pagination table={table} />

            <div>
              <GlobalFilter table={table} />
            </div>
          </div>
        }
      />
    </div>
  );
}
